import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
    User, Package, LogOut, ShoppingBag, Clock, CheckCircle, ChevronDown, ChevronUp,
    MapPin, Phone, CreditCard, Truck, BadgePercent, IndianRupee, AlertCircle, FileText,
    Settings, Home, Star, HelpCircle, Plus, Edit2, Trash2, Shield, Eye, EyeOff,
    Search, Filter, ChevronRight, Heart, MessageSquare, LifeBuoy, Mail, Lock, X, Check, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useAuth, API } from '../App';
import { toast } from 'sonner';

// ==================== OVERVIEW SECTION ====================
const OverviewSection = ({ user, orders, setActiveTab }) => {
    const totalOrders = orders.length;
    const activeOrders = orders.filter(o => !['delivered', 'cancelled'].includes(o.status)).length;
    const totalSpent = orders.reduce((sum, o) => sum + (o.total_amount || 0), 0);
    const recentOrders = orders.slice(0, 3);

    const stats = [
        { label: 'Total Orders', value: totalOrders, icon: Package, color: 'text-blue-600 bg-blue-100' },
        { label: 'Active Orders', value: activeOrders, icon: Truck, color: 'text-orange-600 bg-orange-100' },
        { label: 'Total Spent', value: `₹${totalSpent.toLocaleString()}`, icon: IndianRupee, color: 'text-green-600 bg-green-100' },
    ];

    const quickActions = [
        { label: 'Browse Shop', icon: ShoppingBag, to: '/shop', color: 'from-orange-500 to-amber-500' },
        { label: 'Solar Calculator', icon: IndianRupee, to: '/calculator', color: 'from-green-500 to-emerald-500' },
        { label: 'My Wishlist', icon: Heart, to: '/wishlist', color: 'from-pink-500 to-rose-500' },
        { label: 'PM Surya Ghar', icon: BadgePercent, to: '/subsidy', color: 'from-blue-500 to-indigo-500' },
    ];

    return (
        <div className="space-y-4 md:space-y-6">
            {/* Welcome */}
            <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/5 rounded-xl md:rounded-2xl p-4 md:p-6 border border-primary/20">
                <h2 className="text-lg md:text-xl font-bold mb-1">Welcome back, {user?.name?.split(' ')[0]}! 👋</h2>
                <p className="text-muted-foreground text-xs md:text-sm">Here's what's happening with your solar journey.</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 md:gap-4">
                {stats.map((s, i) => (
                    <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                        className="bg-card rounded-xl border p-3 md:p-4 text-center">
                        <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full ${s.color} flex items-center justify-center mx-auto mb-1.5 md:mb-2`}>
                            <s.icon className="w-4 h-4 md:w-5 md:h-5" />
                        </div>
                        <p className="text-sm md:text-lg font-bold truncate">{s.value}</p>
                        <p className="text-[10px] md:text-xs text-muted-foreground">{s.label}</p>
                    </motion.div>
                ))}
            </div>

            {/* Quick Actions */}
            <div>
                <h3 className="font-semibold mb-3">Quick Actions</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 md:gap-3">
                    {quickActions.map((a, i) => (
                        <Link key={i} to={a.to} className={`bg-gradient-to-br ${a.color} text-white rounded-xl p-3 md:p-4 flex flex-col items-center gap-1.5 md:gap-2 hover:scale-105 active:scale-95 transition-transform`}>
                            <a.icon className="w-5 h-5 md:w-6 md:h-6" />
                            <span className="text-[11px] md:text-xs font-medium">{a.label}</span>
                        </Link>
                    ))}
                </div>
            </div>

            {/* Recent Orders */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold">Recent Orders</h3>
                    <button onClick={() => setActiveTab('orders')} className="text-sm text-primary hover:underline flex items-center gap-1">
                        View All <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
                {recentOrders.length > 0 ? (
                    <div className="space-y-3">
                        {recentOrders.map(order => (
                            <div key={order.id} className="bg-card rounded-xl border p-3 md:p-4 flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
                                    <div className="w-9 h-9 md:w-10 md:h-10 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                                        <Package className="w-4 h-4 md:w-5 md:h-5 text-muted-foreground" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-medium text-sm truncate">{order.items?.[0]?.name || 'Order'}</p>
                                        <p className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</p>
                                    </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <p className="font-semibold text-sm">₹{order.total_amount?.toLocaleString()}</p>
                                    <Badge className={`text-[10px] ${getStatusColor(order.status)}`}>{order.status}</Badge>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8 bg-card rounded-xl border">
                        <ShoppingBag className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                        <p className="text-muted-foreground text-sm">No orders yet</p>
                        <Button size="sm" className="btn-primary mt-3" onClick={() => navigate('/shop')}>Start Shopping</Button>
                    </div>
                )}
            </div>
        </div>
    );
};

// ==================== ORDERS SECTION ====================
const OrdersSection = ({ orders, loading }) => {
    const [filter, setFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedOrder, setExpandedOrder] = useState(null);

    const filtered = orders.filter(o => {
        if (filter !== 'all' && o.status !== filter) return false;
        if (searchQuery && !o.id?.toLowerCase().includes(searchQuery.toLowerCase()) &&
            !o.items?.some(i => i.name?.toLowerCase().includes(searchQuery.toLowerCase()))) return false;
        return true;
    });

    const filters = [
        { key: 'all', label: 'All' }, { key: 'pending', label: 'Pending' },
        { key: 'confirmed', label: 'Confirmed' }, { key: 'processing', label: 'Processing' },
        { key: 'shipped', label: 'Shipped' }, { key: 'delivered', label: 'Delivered' },
    ];

    return (
        <div className="space-y-4">
            <h2 className="text-xl font-bold">My Orders</h2>

            {/* Search + Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder="Search orders..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                        className="pl-9" />
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                    {filters.map(f => (
                        <button key={f.key} onClick={() => setFilter(f.key)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${filter === f.key ? 'bg-primary text-white' : 'bg-secondary hover:bg-secondary/80'}`}>
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Orders List */}
            {loading ? (
                <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="bg-card rounded-xl border p-6 animate-pulse"><div className="h-4 bg-secondary rounded w-1/4 mb-4" /><div className="h-6 bg-secondary rounded w-1/2" /></div>)}</div>
            ) : filtered.length > 0 ? (
                <div className="space-y-4">
                    {filtered.map(order => (
                        <OrderCard key={order.id} order={order} expanded={expandedOrder === order.id}
                            onToggle={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)} />
                    ))}
                </div>
            ) : (
                <div className="text-center py-12 bg-card rounded-xl border">
                    <Package className="w-14 h-14 mx-auto text-muted-foreground/30 mb-3" />
                    <h3 className="font-semibold mb-1">{searchQuery || filter !== 'all' ? 'No matching orders' : 'No orders yet'}</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                        {searchQuery || filter !== 'all' ? 'Try different filters' : 'Start shopping for solar solutions!'}
                    </p>
                    {!searchQuery && filter === 'all' && <Button className="btn-primary" onClick={() => navigate('/shop')}>Browse Products</Button>}
                </div>
            )}
        </div>
    );
};

const OrderCard = ({ order, expanded, onToggle }) => (
    <div className="bg-card rounded-xl border overflow-hidden">
        <div className="p-3.5 md:p-5">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                <div>
                    <p className="text-xs md:text-sm text-muted-foreground">Order #{String(order.id).slice(-8)}</p>
                    <p className="text-[11px] md:text-xs text-muted-foreground">{new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                </div>
                <Badge className={`text-[10px] md:text-xs ${getStatusColor(order.status)}`}>{order.status}</Badge>
            </div>
            {order.items?.slice(0, expanded ? undefined : 2).map((item, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b last:border-0 gap-2">
                    <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
                        <div className="w-10 h-10 md:w-11 md:h-11 bg-secondary rounded-lg overflow-hidden flex-shrink-0">
                            {item.image_url ? <img src={item.image_url.startsWith('/uploads') ? `${API}${item.image_url}` : item.image_url} alt={item.name} className="w-full h-full object-cover" /> :
                                <div className="w-full h-full flex items-center justify-center"><Package className="w-4 h-4 md:w-5 md:h-5 text-muted-foreground" /></div>}
                        </div>
                        <div className="min-w-0"><p className="font-medium text-sm truncate">{item.name}</p><p className="text-xs text-muted-foreground">Qty: {item.quantity}</p></div>
                    </div>
                    <p className="font-semibold text-sm flex-shrink-0">₹{((item.price || 0) * (item.quantity || 1)).toLocaleString()}</p>
                </div>
            ))}
            {order.items?.length > 2 && !expanded && <p className="text-xs text-muted-foreground text-center py-2">+ {order.items.length - 2} more items</p>}
            <div className="flex justify-between items-center mt-3 pt-3 border-t">
                <span className="font-semibold text-sm md:text-base">Total: ₹{order.total_amount?.toLocaleString()}</span>
                <Button variant="outline" size="sm" onClick={onToggle} className="text-xs">
                    {expanded ? <>Hide<ChevronUp className="w-3.5 h-3.5 ml-1" /></> : <>Details<ChevronDown className="w-3.5 h-3.5 ml-1" /></>}
                </Button>
            </div>
        </div>

        {/* Subsidy Tracker */}
        {order.claim_subsidy && <SubsidyTracker order={order} />}

        {/* Expanded Details */}
        <AnimatePresence>
            {expanded && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="border-t bg-muted/30 overflow-hidden">
                    <div className="p-3.5 md:p-5 grid md:grid-cols-2 gap-4 md:gap-5">
                        <div className="space-y-2">
                            <h4 className="font-semibold text-sm flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" />Shipping Address</h4>
                            <div className="text-sm text-muted-foreground">
                                {typeof order.shipping_address === 'string' ? <p>{order.shipping_address}</p> : order.shipping_address ? (
                                    <><p className="font-medium text-foreground">{order.shipping_address.name}</p>
                                        <p>{order.shipping_address.address}</p>
                                        <p>{order.shipping_address.city}, {order.shipping_address.state} - {order.shipping_address.pincode}</p>
                                        {order.shipping_address.phone && <p className="flex items-center gap-1 mt-1"><Phone className="w-3 h-3" />{order.shipping_address.phone}</p>}
                                    </>
                                ) : <p>Not available</p>}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <h4 className="font-semibold text-sm flex items-center gap-2"><CreditCard className="w-4 h-4 text-primary" />Payment</h4>
                            <div className="text-sm space-y-1">
                                <div className="flex justify-between"><span className="text-muted-foreground">Method</span><span className="font-medium capitalize">{order.payment_method || 'Online'}</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge variant="outline" className={order.payment_status === 'paid' ? 'text-green-600' : 'text-yellow-600'}>{order.payment_status || 'Pending'}</Badge></div>
                            </div>
                        </div>
                        {order.shipping_info && (
                            <div className="md:col-span-2 bg-purple-50 dark:bg-purple-950/20 rounded-xl border border-purple-200 dark:border-purple-800 p-4">
                                <h4 className="font-semibold text-sm flex items-center gap-2 text-purple-700 dark:text-purple-300 mb-2"><Truck className="w-4 h-4" />Delivery Tracking</h4>
                                <div className="grid md:grid-cols-2 gap-3 text-sm">
                                    <div className="flex justify-between"><span className="text-muted-foreground">Person</span><span className="font-medium">{order.shipping_info.delivery_person_name}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Contact</span><a href={`tel:${order.shipping_info.delivery_person_phone}`} className="text-primary font-medium">{order.shipping_info.delivery_person_phone}</a></div>
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    </div>
);

const SubsidyTracker = ({ order }) => {
    const steps = [
        { key: 'pending', label: 'Submitted', icon: FileText },
        { key: 'submitted', label: 'Sent to MNRE', icon: FileText },
        { key: 'under_review', label: 'Under Review', icon: Clock },
        { key: 'approved', label: 'Approved', icon: CheckCircle },
        { key: 'disbursed', label: 'Disbursed', icon: IndianRupee }
    ];
    const rejected = order.subsidy_status === 'rejected';
    const idx = Math.max(0, steps.findIndex(s => s.key === order.subsidy_status));

    return (
        <div className="border-t bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 px-3.5 md:px-5 py-3 md:py-4">
            <div className="flex items-center justify-between mb-3 gap-2">
                <div className="flex items-center gap-1.5 md:gap-2 min-w-0"><BadgePercent className="w-4 h-4 text-green-600 flex-shrink-0" /><span className="text-xs md:text-sm font-semibold text-green-800 dark:text-green-200 truncate">PM Surya Ghar Subsidy</span></div>
                <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
                    {order.subsidy_amount > 0 && <span className="text-sm font-bold text-green-700 dark:text-green-300">₹{order.subsidy_amount.toLocaleString()}</span>}
                    <Badge className={rejected ? 'bg-red-100 text-red-700' : order.subsidy_status === 'disbursed' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}>
                        {rejected ? 'REJECTED' : (order.subsidy_status || 'pending').replaceAll('_', ' ').toUpperCase()}
                    </Badge>
                </div>
            </div>
            {rejected ? (
                <div className="bg-red-100 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-red-700 dark:text-red-300"><AlertCircle className="w-4 h-4" /><span className="text-sm font-medium">Application rejected.</span></div>
                    {order.subsidy_notes && <p className="text-xs text-red-600 dark:text-red-400 mt-1 ml-6">{order.subsidy_notes}</p>}
                </div>
            ) : (
                <div className="flex items-center gap-0.5 md:gap-1 overflow-x-auto no-scrollbar">
                    {steps.map((step, i) => (
                        <React.Fragment key={step.key}>
                            <div className={`flex flex-col items-center flex-shrink-0 ${i === idx ? 'scale-105 md:scale-110' : ''}`}>
                                <div className={`w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center border-2 transition-all ${i <= idx ? 'bg-green-500 border-green-500 text-white' : 'bg-white dark:bg-card border-gray-300 text-gray-400'}`}>
                                    <step.icon className="w-3 h-3 md:w-3.5 md:h-3.5" />
                                </div>
                                <span className={`text-[9px] md:text-[10px] mt-0.5 md:mt-1 text-center leading-tight max-w-[48px] md:max-w-none ${i <= idx ? 'text-green-700 dark:text-green-300 font-medium' : 'text-gray-400'}`}>{step.label}</span>
                            </div>
                            {i < steps.length - 1 && <div className={`flex-1 min-w-[12px] md:min-w-[20px] h-0.5 mt-[-12px] ${i < idx ? 'bg-green-500' : 'bg-gray-300'}`} />}
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
};

// ==================== ADDRESSES SECTION ====================
const AddressesSection = () => {
    const [addresses, setAddresses] = useState(() => {
        try { return JSON.parse(localStorage.getItem('user_addresses') || '[]'); } catch { return []; }
    });
    const [editing, setEditing] = useState(null); // null | 'new' | address id
    const [form, setForm] = useState({ label: 'Home', name: '', phone: '', address: '', city: '', state: '', pincode: '' });

    const save = () => {
        if (!form.name || !form.address || !form.city || !form.state || !form.pincode || !form.phone) {
            toast.error('Please fill all fields'); return;
        }
        let updated;
        if (editing === 'new') {
            updated = [...addresses, { ...form, id: Date.now().toString(), isDefault: addresses.length === 0 }];
        } else {
            updated = addresses.map(a => a.id === editing ? { ...a, ...form } : a);
        }
        setAddresses(updated);
        localStorage.setItem('user_addresses', JSON.stringify(updated));
        setEditing(null);
        setForm({ label: 'Home', name: '', phone: '', address: '', city: '', state: '', pincode: '' });
        toast.success(editing === 'new' ? 'Address added!' : 'Address updated!');
    };

    const remove = (id) => {
        const updated = addresses.filter(a => a.id !== id);
        if (updated.length > 0 && !updated.some(a => a.isDefault)) updated[0].isDefault = true;
        setAddresses(updated);
        localStorage.setItem('user_addresses', JSON.stringify(updated));
        toast.success('Address removed');
    };

    const setDefault = (id) => {
        const updated = addresses.map(a => ({ ...a, isDefault: a.id === id }));
        setAddresses(updated);
        localStorage.setItem('user_addresses', JSON.stringify(updated));
        toast.success('Default address updated');
    };

    const startEdit = (addr) => {
        setForm({ label: addr.label, name: addr.name, phone: addr.phone, address: addr.address, city: addr.city, state: addr.state, pincode: addr.pincode });
        setEditing(addr.id);
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">My Addresses</h2>
                <Button size="sm" className="btn-primary" onClick={() => { setForm({ label: 'Home', name: '', phone: '', address: '', city: '', state: '', pincode: '' }); setEditing('new'); }}>
                    <Plus className="w-4 h-4 mr-1" />Add New
                </Button>
            </div>

            {/* Add/Edit Form */}
            <AnimatePresence>
                {editing && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="bg-card rounded-xl border p-4 md:p-5 overflow-hidden">
                        <h3 className="font-semibold mb-3 md:mb-4 text-sm md:text-base">{editing === 'new' ? 'Add New Address' : 'Edit Address'}</h3>
                        <div className="grid gap-3 md:gap-4">
                            <div className="flex gap-2">
                                {['Home', 'Office', 'Other'].map(l => (
                                    <button key={l} onClick={() => setForm({ ...form, label: l })}
                                        className={`px-3 md:px-4 py-1.5 rounded-full text-xs font-medium ${form.label === l ? 'bg-primary text-white' : 'bg-secondary'}`}>{l}</button>
                                ))}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Input placeholder="Full Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                                <Input placeholder="Phone Number" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                            </div>
                            <Input placeholder="Full Address (House No, Street, Landmark)" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
                                <Input placeholder="City" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} />
                                <Input placeholder="State" value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} />
                                <Input placeholder="PIN Code" value={form.pincode} onChange={e => setForm({ ...form, pincode: e.target.value })} className="col-span-2 md:col-span-1" />
                            </div>
                            <div className="flex gap-2">
                                <Button onClick={save} className="btn-primary flex-1 md:flex-none">Save Address</Button>
                                <Button variant="outline" onClick={() => setEditing(null)} className="flex-1 md:flex-none">Cancel</Button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Addresses List */}
            {addresses.length > 0 ? (
                <div className="grid gap-4">
                    {[...addresses].sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0)).map(addr => (
                        <div key={addr.id} className={`bg-card rounded-xl border p-4 md:p-5 relative ${addr.isDefault ? 'border-primary/50 ring-1 ring-primary/20' : ''}`}>
                            {addr.isDefault && <Badge className="absolute top-2.5 right-2.5 md:top-3 md:right-3 bg-primary/10 text-primary text-[10px]">DEFAULT</Badge>}
                            <div className="flex items-start gap-2.5 md:gap-3">
                                <div className="w-9 h-9 md:w-10 md:h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <MapPin className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <span className="font-semibold text-sm md:text-base">{addr.name}</span>
                                        <Badge variant="outline" className="text-[10px]">{addr.label}</Badge>
                                    </div>
                                    <p className="text-xs md:text-sm text-muted-foreground">{addr.address}</p>
                                    <p className="text-xs md:text-sm text-muted-foreground">{addr.city}, {addr.state} - {addr.pincode}</p>
                                    <p className="text-xs md:text-sm text-muted-foreground flex items-center gap-1 mt-1"><Phone className="w-3 h-3" />{addr.phone}</p>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
                                <Button variant="outline" size="sm" onClick={() => startEdit(addr)} className="text-xs h-8"><Edit2 className="w-3 h-3 mr-1" />Edit</Button>
                                {!addr.isDefault && <Button variant="outline" size="sm" onClick={() => setDefault(addr.id)} className="text-xs h-8"><Check className="w-3 h-3 mr-1" />Set Default</Button>}
                                <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50 text-xs h-8" onClick={() => remove(addr.id)}><Trash2 className="w-3 h-3 mr-1" />Remove</Button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : !editing && (
                <div className="text-center py-12 bg-card rounded-xl border">
                    <MapPin className="w-14 h-14 mx-auto text-muted-foreground/30 mb-3" />
                    <h3 className="font-semibold mb-1">No saved addresses</h3>
                    <p className="text-sm text-muted-foreground">Add an address for faster checkout</p>
                </div>
            )}
        </div>
    );
};

// ==================== PROFILE SECTION ====================
const ProfileSection = ({ user, token }) => {
    const [editMode, setEditMode] = useState(false);
    const [name, setName] = useState(user?.name || '');
    const [phone, setPhone] = useState('');
    const [saving, setSaving] = useState(false);
    const [showPwForm, setShowPwForm] = useState(false);
    const [pw, setPw] = useState({ current: '', new: '', confirm: '' });
    const [showPw, setShowPw] = useState(false);
    const [pwSaving, setPwSaving] = useState(false);
    const [solarEditing, setSolarEditing] = useState(false);
    const [solarProfile, setSolarProfile] = useState(() => {
        try { return JSON.parse(localStorage.getItem('user_profile_data') || '{}'); } catch { return {}; }
    });
    const [solarForm, setSolarForm] = useState({
        monthlyBill: solarProfile.solarInfo?.monthlyElectricityBill || '',
        homeSpace: solarProfile.solarInfo?.homeSpaceSqFt || '',
        roofType: solarProfile.solarInfo?.roofType || 'flat',
        propertyType: solarProfile.solarInfo?.propertyType || 'residential',
    });

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
                setPhone(res.data.phone || '');
            } catch { }
        };
        fetchProfile();
    }, [token]);

    const saveProfile = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/api/auth/profile`, { name, phone }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Profile updated!');
            setEditMode(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Update failed');
        } finally { setSaving(false); }
    };

    const saveSolarInfo = () => {
        const updated = { ...solarProfile, name: solarProfile.name || user?.name, email: solarProfile.email || user?.email, solarInfo: { ...solarProfile.solarInfo, monthlyElectricityBill: solarForm.monthlyBill, homeSpaceSqFt: solarForm.homeSpace, roofType: solarForm.roofType, propertyType: solarForm.propertyType } };
        localStorage.setItem('user_profile_data', JSON.stringify(updated));
        setSolarProfile(updated);
        setSolarEditing(false);
        toast.success('Solar details updated!');
    };

    const changePassword = async () => {
        if (pw.new !== pw.confirm) { toast.error('Passwords do not match'); return; }
        if (pw.new.length < 6) { toast.error('Password must be at least 6 characters'); return; }
        setPwSaving(true);
        try {
            await axios.put(`${API}/api/auth/change-password`, { current_password: pw.current, new_password: pw.new }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Password changed!');
            setShowPwForm(false);
            setPw({ current: '', new: '', confirm: '' });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to change password');
        } finally { setPwSaving(false); }
    };

    return (
        <div className="space-y-6">
            <h2 className="text-xl font-bold">Profile & Settings</h2>

            {/* Personal Info */}
            <div className="bg-card rounded-xl border p-4 md:p-6">
                <div className="flex items-center justify-between mb-4 md:mb-5">
                    <h3 className="font-semibold flex items-center gap-2 text-sm md:text-base"><User className="w-4 h-4 md:w-5 md:h-5 text-primary" />Personal Information</h3>
                    {!editMode && <Button variant="ghost" size="sm" onClick={() => setEditMode(true)} className="text-xs"><Edit2 className="w-3.5 h-3.5 mr-1" />Edit</Button>}
                </div>
                <div className="flex items-center gap-3 md:gap-4 mb-5 md:mb-6">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center text-white text-lg md:text-2xl font-bold flex-shrink-0">{user?.name?.[0]?.toUpperCase() || 'U'}</div>
                    <div className="min-w-0">
                        <p className="text-base md:text-lg font-semibold truncate">{user?.name}</p>
                        <p className="text-xs md:text-sm text-muted-foreground">Member since {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) : '—'}</p>
                    </div>
                </div>
                {editMode ? (
                    <div className="space-y-4">
                        <div><label className="text-sm font-medium mb-1 block">Full Name</label><Input value={name} onChange={e => setName(e.target.value)} /></div>
                        <div><label className="text-sm font-medium mb-1 block">Email</label><Input value={user?.email} disabled className="bg-muted" /><p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p></div>
                        <div><label className="text-sm font-medium mb-1 block">Phone</label><Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Enter phone number" /></div>
                        <div className="flex gap-2">
                            <Button onClick={saveProfile} disabled={saving} className="btn-primary">{saving ? 'Saving...' : 'Save Changes'}</Button>
                            <Button variant="outline" onClick={() => { setEditMode(false); setName(user?.name || ''); }}>Cancel</Button>
                        </div>
                    </div>
                ) : (
                    <div className="grid gap-3">
                        {[{ icon: Mail, label: 'Email', value: user?.email }, { icon: Phone, label: 'Phone', value: phone || 'Not added' }, { icon: Shield, label: 'Role', value: user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1) }].map((item, i) => (
                            <div key={i} className="flex items-center gap-3 py-2 border-b last:border-0">
                                <item.icon className="w-4 h-4 text-muted-foreground" />
                                <span className="text-sm text-muted-foreground w-20">{item.label}</span>
                                <span className="text-sm font-medium">{item.value}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Solar Details */}
            <div className="bg-card rounded-xl border p-4 md:p-6">
                <div className="flex items-center justify-between mb-4 md:mb-5">
                    <h3 className="font-semibold flex items-center gap-2 text-sm md:text-base"><Zap className="w-4 h-4 md:w-5 md:h-5 text-primary" />Solar Details</h3>
                    {!solarEditing && <Button variant="ghost" size="sm" onClick={() => setSolarEditing(true)} className="text-xs"><Edit2 className="w-3.5 h-3.5 mr-1" />Edit</Button>}
                </div>
                {solarEditing ? (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="text-sm font-medium mb-1 block">Monthly Bill (₹)</label><Input type="number" value={solarForm.monthlyBill} onChange={e => setSolarForm({ ...solarForm, monthlyBill: e.target.value })} placeholder="e.g. 3000" /></div>
                            <div><label className="text-sm font-medium mb-1 block">Home Space (sq ft)</label><Input type="number" value={solarForm.homeSpace} onChange={e => setSolarForm({ ...solarForm, homeSpace: e.target.value })} placeholder="e.g. 1000" /></div>
                            <div><label className="text-sm font-medium mb-1 block">Roof Type</label><select value={solarForm.roofType} onChange={e => setSolarForm({ ...solarForm, roofType: e.target.value })} className="w-full h-10 px-3 text-sm rounded-md border border-input bg-background"><option value="flat">Flat</option><option value="sloped">Sloped</option><option value="mixed">Mixed</option></select></div>
                            <div><label className="text-sm font-medium mb-1 block">Property Type</label><select value={solarForm.propertyType} onChange={e => setSolarForm({ ...solarForm, propertyType: e.target.value })} className="w-full h-10 px-3 text-sm rounded-md border border-input bg-background"><option value="residential">Residential</option><option value="commercial">Commercial</option><option value="industrial">Industrial</option></select></div>
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={saveSolarInfo} className="btn-primary">Save Solar Details</Button>
                            <Button variant="outline" onClick={() => setSolarEditing(false)}>Cancel</Button>
                        </div>
                    </div>
                ) : solarProfile.solarInfo ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                        {[
                            { label: 'Monthly Bill', value: `₹${solarProfile.solarInfo.monthlyElectricityBill || '—'}`, emoji: '💰' },
                            { label: 'Home Space', value: `${solarProfile.solarInfo.homeSpaceSqFt || '—'} sq ft`, emoji: '🏠' },
                            { label: 'Roof Type', value: solarProfile.solarInfo.roofType || '—', emoji: '🔝' },
                            { label: 'Property Type', value: solarProfile.solarInfo.propertyType || '—', emoji: '🏢' },
                        ].map((item, i) => (
                            <div key={i} className="bg-muted/50 rounded-lg p-3">
                                <p className="text-xs text-muted-foreground flex items-center gap-1">{item.emoji} {item.label}</p>
                                <p className="font-medium text-sm capitalize mt-0.5">{item.value}</p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-6">
                        <Zap className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                        <p className="text-sm text-muted-foreground mb-3">No solar details added yet</p>
                        <Button size="sm" variant="outline" onClick={() => setSolarEditing(true)}>Add Solar Details</Button>
                    </div>
                )}
            </div>

            {/* Password */}
            <div className="bg-card rounded-xl border p-4 md:p-6">
                <div className="flex items-center justify-between mb-4 gap-2">
                    <h3 className="font-semibold flex items-center gap-2 text-sm md:text-base"><Lock className="w-4 h-4 md:w-5 md:h-5 text-primary" />Password & Security</h3>
                    {!showPwForm && <Button variant="outline" size="sm" onClick={() => setShowPwForm(true)} className="text-xs">Change Password</Button>}
                </div>
                {showPwForm ? (
                    <div className="space-y-4">
                        <div className="relative">
                            <Input type={showPw ? 'text' : 'password'} placeholder="Current Password" value={pw.current} onChange={e => setPw({ ...pw, current: e.target.value })} />
                            <button onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                        </div>
                        <Input type="password" placeholder="New Password (min 6 chars)" value={pw.new} onChange={e => setPw({ ...pw, new: e.target.value })} />
                        <Input type="password" placeholder="Confirm New Password" value={pw.confirm} onChange={e => setPw({ ...pw, confirm: e.target.value })} />
                        <div className="flex gap-2">
                            <Button onClick={changePassword} disabled={pwSaving} className="btn-primary">{pwSaving ? 'Changing...' : 'Update Password'}</Button>
                            <Button variant="outline" onClick={() => { setShowPwForm(false); setPw({ current: '', new: '', confirm: '' }); }}>Cancel</Button>
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-muted-foreground">Password last changed: Unknown · We recommend changing your password regularly.</p>
                )}
            </div>
        </div>
    );
};

// ==================== REVIEWS SECTION ====================
const ReviewsSection = ({ token }) => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchReviews = async () => {
            try {
                // Fetch all products to get user's reviews
                const res = await axios.get(`${API}/api/products`);
                const allProducts = res.data;
                const userReviews = [];
                for (const p of allProducts) {
                    try {
                        const rRes = await axios.get(`${API}/api/reviews/${p.id}`);
                        rRes.data.forEach(r => {
                            if (r.user_id || r.user_name) {
                                userReviews.push({ ...r, product_name: p.name, product_id: p.id, product_image: p.image_url });
                            }
                        });
                    } catch { }
                }
                setReviews(userReviews.slice(0, 20));
            } catch { }
            setLoading(false);
        };
        fetchReviews();
    }, [token]);

    return (
        <div className="space-y-4">
            <h2 className="text-xl font-bold">My Reviews</h2>
            {loading ? (
                <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="bg-card rounded-xl border p-5 animate-pulse"><div className="h-4 bg-secondary rounded w-1/3 mb-2" /><div className="h-3 bg-secondary rounded w-2/3" /></div>)}</div>
            ) : reviews.length > 0 ? (
                <div className="space-y-4">
                    {reviews.map((r, i) => (
                        <div key={i} className="bg-card rounded-xl border p-5">
                            <div className="flex items-start gap-3">
                                <div className="w-12 h-12 bg-secondary rounded-lg overflow-hidden flex-shrink-0">
                                    {r.product_image ? <img src={r.product_image.startsWith('/uploads') ? `${API}${r.product_image}` : r.product_image} alt="" className="w-full h-full object-cover" /> :
                                        <div className="w-full h-full flex items-center justify-center"><Package className="w-5 h-5 text-muted-foreground" /></div>}
                                </div>
                                <div className="flex-1">
                                    <Link to={`/product/${r.product_id}`} className="font-medium text-sm hover:text-primary">{r.product_name}</Link>
                                    <div className="flex items-center gap-1 mt-1">
                                        {[...Array(5)].map((_, si) => <Star key={si} className={`w-3.5 h-3.5 ${si < r.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />)}
                                        <span className="text-xs text-muted-foreground ml-1">{new Date(r.created_at).toLocaleDateString()}</span>
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-1">{r.comment}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-12 bg-card rounded-xl border">
                    <Star className="w-14 h-14 mx-auto text-muted-foreground/30 mb-3" />
                    <h3 className="font-semibold mb-1">No reviews yet</h3>
                    <p className="text-sm text-muted-foreground">Purchase a product and share your experience!</p>
                </div>
            )}
        </div>
    );
};

// ==================== HELP SECTION ====================
const HelpSection = () => {
    const [openFaq, setOpenFaq] = useState(null);
    const faqs = [
        { q: 'How do I track my order?', a: 'Go to "My Orders" in your dashboard. Click "View Details" on any order to see real-time tracking, delivery person details, and estimated delivery date.' },
        { q: 'How does PM Surya Ghar subsidy work?', a: 'Orders with subsidy-eligible products can claim up to ₹78,000 under PM Surya Ghar scheme. Select "Claim Subsidy" at checkout and provide your Aadhaar & bank details. We handle the application process.' },
        { q: 'What is the warranty on solar panels?', a: 'Most solar panels come with a 25-year performance warranty and 10-year product warranty. Inverters typically have 5-10 year warranties. Check individual product pages for specifics.' },
        { q: 'How do I cancel or return an order?', a: 'Contact our support team within 7 days of delivery. Go to Help & Support > Contact Us or call us directly. Cancellation before shipping is free.' },
        { q: 'Do you provide installation services?', a: 'Yes! All solar system purchases include free professional installation by our certified technicians. You can also request an expert visit before purchasing.' },
        { q: 'What payment methods are accepted?', a: 'We accept all major credit/debit cards, UPI, net banking, and wallets through Razorpay. EMI options are available on select products.' },
    ];

    return (
        <div className="space-y-6">
            <h2 className="text-xl font-bold">Help & Support</h2>

            {/* Contact Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                {[
                    { icon: Phone, label: 'Call Us', value: '+91 98765 43210', href: 'tel:+919876543210', color: 'bg-green-100 text-green-600' },
                    { icon: Mail, label: 'Email', value: 'support@solarsavers.in', href: 'mailto:support@solarsavers.in', color: 'bg-blue-100 text-blue-600' },
                    { icon: MessageSquare, label: 'WhatsApp', value: 'Chat with us', href: 'https://wa.me/919876543210', color: 'bg-emerald-100 text-emerald-600' },
                ].map((c, i) => (
                    <a key={i} href={c.href} target="_blank" rel="noopener noreferrer" className="bg-card rounded-xl border p-4 md:p-5 flex items-center gap-3 md:gap-4 hover:border-primary/50 active:scale-[0.98] transition-all">
                        <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full ${c.color} flex items-center justify-center flex-shrink-0`}><c.icon className="w-4 h-4 md:w-5 md:h-5" /></div>
                        <div className="min-w-0"><p className="font-semibold text-sm">{c.label}</p><p className="text-xs text-muted-foreground truncate">{c.value}</p></div>
                    </a>
                ))}
            </div>

            {/* Raise Ticket */}
            <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl border border-primary/20 p-5">
                <div className="flex items-center gap-3 mb-2">
                    <LifeBuoy className="w-6 h-6 text-primary" />
                    <div>
                        <h3 className="font-semibold">Need more help?</h3>
                        <p className="text-sm text-muted-foreground">Raise a support ticket and our team will get back to you within 24 hours.</p>
                    </div>
                </div>
                <Button size="sm" className="btn-primary mt-2" onClick={() => navigate('/contact')}>Contact Support</Button>
            </div>

            {/* FAQs */}
            <div>
                <h3 className="font-semibold mb-3">Frequently Asked Questions</h3>
                <div className="space-y-2">
                    {faqs.map((faq, i) => (
                        <div key={i} className="bg-card rounded-xl border overflow-hidden">
                            <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-4 text-left">
                                <span className="font-medium text-sm pr-4">{faq.q}</span>
                                <ChevronDown className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                            </button>
                            <AnimatePresence>
                                {openFaq === i && (
                                    <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                                        <p className="px-4 pb-4 text-sm text-muted-foreground">{faq.a}</p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ==================== HELPER ====================
const getStatusColor = (status) => {
    const colors = { pending: 'bg-yellow-100 text-yellow-800', confirmed: 'bg-blue-100 text-blue-800', processing: 'bg-blue-100 text-blue-800', shipped: 'bg-purple-100 text-purple-800', delivered: 'bg-green-100 text-green-800', completed: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800' };
    return colors[status] || 'bg-gray-100 text-gray-800';
};

// ==================== MAIN DASHBOARD ====================
const UserDashboard = () => {
    const navigate = useNavigate();
    const { user, token, logout } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const tabScrollRef = React.useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    useEffect(() => { if (token) fetchOrders(); }, [token]);
    const fetchOrders = async () => {
        try {
            const res = await axios.get(`${API}/api/orders`, { headers: { Authorization: `Bearer ${token}` } });
            setOrders(res.data);
        } catch (e) { console.error('Failed to fetch orders:', e); }
        finally { setLoading(false); }
    };

    const checkTabScroll = () => {
        const el = tabScrollRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 8);
        setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
    };

    useEffect(() => {
        const el = tabScrollRef.current;
        if (!el) return;
        checkTabScroll();
        el.addEventListener('scroll', checkTabScroll, { passive: true });
        window.addEventListener('resize', checkTabScroll);
        return () => { el.removeEventListener('scroll', checkTabScroll); window.removeEventListener('resize', checkTabScroll); };
    }, []);

    const scrollTabs = (dir) => {
        const el = tabScrollRef.current;
        if (el) el.scrollBy({ left: dir * 120, behavior: 'smooth' });
    };

    const tabs = [
        { key: 'overview', label: 'Overview', icon: Home },
        { key: 'orders', label: 'My Orders', icon: Package },
        { key: 'addresses', label: 'Addresses', icon: MapPin },
        { key: 'profile', label: 'Profile', icon: Settings },
        { key: 'reviews', label: 'Reviews', icon: Star },
        { key: 'help', label: 'Help', icon: HelpCircle },
    ];

    return (
        <div className="min-h-screen bg-secondary/30 overflow-x-hidden">
            {/* Mobile Tab Bar — Fixed right below header, ZERO gap */}
            <div className="lg:hidden fixed top-14 left-0 right-0 z-30 bg-background/95 backdrop-blur-md border-b">
                <div className="relative">
                    {/* Left scroll arrow */}
                    {canScrollLeft && (
                        <button onClick={() => scrollTabs(-1)} className="absolute left-0 top-0 bottom-0 z-10 w-7 flex items-center justify-center bg-gradient-to-r from-background via-background/90 to-transparent">
                            <ChevronRight className="w-4 h-4 text-muted-foreground rotate-180" />
                        </button>
                    )}
                    {/* Right scroll arrow */}
                    {canScrollRight && (
                        <button onClick={() => scrollTabs(1)} className="absolute right-0 top-0 bottom-0 z-10 w-7 flex items-center justify-center bg-gradient-to-l from-background via-background/90 to-transparent">
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </button>
                    )}
                    <div ref={tabScrollRef} className="flex gap-1.5 overflow-x-auto no-scrollbar px-3 py-2">
                        {tabs.map(t => (
                            <button key={t.key} onClick={() => setActiveTab(t.key)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap transition-all ${activeTab === t.key ? 'bg-primary text-white shadow-sm' : 'bg-card border text-muted-foreground'}`}>
                                <t.icon className="w-3.5 h-3.5" />{t.label}
                            </button>
                        ))}
                        <button onClick={logout}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap text-red-600 bg-red-50 border border-red-200 ml-0.5 flex-shrink-0">
                            <LogOut className="w-3.5 h-3.5" />Logout
                        </button>
                    </div>
                </div>
            </div>

            {/* Content — pt accounts for: header (56px) + tab bar (~40px) on mobile; just header on desktop */}
            <div className="container-solar pt-[6.5rem] lg:pt-24 pb-20 lg:pb-8">
                <div className="grid lg:grid-cols-[260px_1fr] gap-4 md:gap-8">

                    {/* Sidebar — Desktop */}
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="hidden lg:block">
                        <div className="bg-card rounded-2xl border p-5 sticky top-24">
                            <div className="flex items-center gap-3 mb-5 pb-5 border-b">
                                <div className="w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center text-white text-xl font-bold">
                                    {user?.name?.[0]?.toUpperCase() || 'U'}
                                </div>
                                <div className="min-w-0">
                                    <h2 className="font-semibold truncate">{user?.name}</h2>
                                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                                </div>
                            </div>
                            <nav className="space-y-1">
                                {tabs.map(t => (
                                    <button key={t.key} onClick={() => setActiveTab(t.key)}
                                        className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${activeTab === t.key ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-secondary text-muted-foreground'}`}>
                                        <t.icon className="w-4.5 h-4.5" />{t.label}
                                    </button>
                                ))}
                                <div className="pt-3 mt-3 border-t">
                                    <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors">
                                        <LogOut className="w-4.5 h-4.5" />Logout
                                    </button>
                                </div>
                            </nav>
                        </div>
                    </motion.div>

                    {/* Content */}
                    <div className="min-w-0">
                        <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
                            {activeTab === 'overview' && <OverviewSection user={user} orders={orders} setActiveTab={setActiveTab} />}
                            {activeTab === 'orders' && <OrdersSection orders={orders} loading={loading} />}
                            {activeTab === 'addresses' && <AddressesSection />}
                            {activeTab === 'profile' && <ProfileSection user={user} token={token} />}
                            {activeTab === 'reviews' && <ReviewsSection token={token} />}
                            {activeTab === 'help' && <HelpSection />}
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDashboard;
